import type React from "react";
import { useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useDatabaseSyncWrapperStore } from "@/components/contexts/databaseSyncStore";
import { useDuplicateStoryboard } from "@/hooks/storyboards/useDuplicateStoryboard";
import { CursorType } from "@/store/globalSettingsSlice";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import { STORYBOARD_TYPES } from "@/utils/constants";
import {
  useCheckDevServerHtmlErrors,
  useDevServerLaunched,
} from "@/utils/devServerUtils";
import { useActiveCanvas, useAutoUpdateRef, useCanvasId } from "@/utils/utils";
import { useStoryboardUrl } from "../hooks/useStoryboardUrl";
import { GLOBAL_DEV_MODE, useIframePoolStore } from "./iframePoolStore";
import {
  handleErrorFromIframe,
  useIframePostMessaging,
} from "./useIframePostMessaging";

interface ApplicationStoryboardIframeProps {
  storyboardId: string;
}

export const ApplicationStoryboardIframe: React.FC<
  ApplicationStoryboardIframeProps
> = ({ storyboardId }) => {
  const DEV_MODE = GLOBAL_DEV_MODE || false;

  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const canvasId = useCanvasId();

  const activeCanvas = useActiveCanvas();
  const storyboard = activeCanvas?.storyboards?.find(
    (s) => s.id === storyboardId,
  );
  const storyboardRef = useAutoUpdateRef(storyboard);

  const appStoryboard = useIframePoolStore(
    (state) => state.applicationStoryboards[storyboardId],
  );

  // Get panel resize state to temporarily disable pointer events
  const isPanelResizing = useIframePoolStore((state) => state.isPanelResizing);

  const storyboardUrl = useStoryboardUrl(storyboard);
  const { onLoadIFrame } = useIframePostMessaging();

  const {
    setApplicationStoryboardContentWindow,
    markApplicationStoryboardAsFailedToLoad,
    resetApplicationStoryboard,
    retryApplicationIframe,
    baseRetryDelay,
    maxRetries,
  } = useIframePoolStore();

  const iframeLoadedRef = useRef(false);

  const fullFilePathRef = useRef(storyboard?.fullFilePath || "");
  useEffect(() => {
    fullFilePathRef.current = storyboard?.fullFilePath || "";
  }, [storyboard?.fullFilePath]);

  const originalStoryboardUrlRef = useRef(storyboardUrl);
  useEffect(() => {
    originalStoryboardUrlRef.current = storyboardUrl;
  }, [storyboardUrl]);

  const cursorType = useSelector(
    (state: any) => state.globalSettings.cursorType,
  );
  const isDriveMode = cursorType === CursorType.Drive;

  const projectLoadingProgress = useSelector(
    (state: any) => state.supabaseStore.projectLoadingProgress,
  );
  const projectLoadingProgressRef = useAutoUpdateRef(projectLoadingProgress);

  // Handle iframe load
  const handleLoad = () => {
    if (!iframeRef.current?.contentWindow) {
      console.error({
        msg: "[TEMPO DEBUG] Application storyboard iframe failed to load - no content window",
        storyboardId,
      });
      markApplicationStoryboardAsFailedToLoad(appStoryboard.iframeId);
      retryApplicationIframe(storyboardId);
      return;
    }

    iframeLoadedRef.current = true;

    // Update store with content window
    setApplicationStoryboardContentWindow(
      storyboardId,
      iframeRef.current.contentWindow,
    );

    const freshAppStoryboard =
      useIframePoolStore.getState().applicationStoryboards[storyboardId];
    if (freshAppStoryboard.status === "loading") {
      // Wait for the iframe to be ready with the script ready message
      // that'll trigger the onLoadIFrame call
      return;
    }

    onLoadIFrame(iframeRef.current?.contentWindow!, {
      storyboardId,
      type: STORYBOARD_TYPES.APPLICATION,
      fullFilePath: fullFilePathRef.current,
      originalStoryboardUrl: originalStoryboardUrlRef.current,
      iframeId: appStoryboard.iframeId,
    });
  };

  const devServerLaunched = useDevServerLaunched();
  const devServerLaunchedRef = useAutoUpdateRef(devServerLaunched);
  const duplicateStoryboard = useDuplicateStoryboard();

  // Listen for messages from this specific iframe
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (event.data.type === "SCRIPT_READY") {
        if (event.source !== iframeRef.current?.contentWindow) {
          return;
        }

        if (
          projectLoadingProgressRef.current?.devtools_script_responsive !==
            true &&
          devServerLaunchedRef.current
        ) {
          await useSupabaseStore
            .getState()
            .supabaseClient?.from("project_loading_progress")
            .update({
              devtools_script_responsive: true,
              last_updated: new Date().toISOString(),
            })
            .eq("canvas_id", canvasId);

          useDatabaseSyncWrapperStore
            .getState()
            .triggerRefetchProjectLoadingProgress();
        }

        useIframePoolStore
          .getState()
          .handleApplicationIframeReady(appStoryboard.iframeId);

        // If the script gets reset, we need to call onLoadIFrame again
        if (iframeRef.current?.contentWindow && iframeLoadedRef.current) {
          onLoadIFrame(iframeRef.current?.contentWindow!, {
            storyboardId,
            type: STORYBOARD_TYPES.APPLICATION,
            fullFilePath: fullFilePathRef.current,
            originalStoryboardUrl: originalStoryboardUrlRef.current,
            iframeId: appStoryboard.iframeId,
          });
        }
      } else if (event.data.type === "ERROR") {
        if (event.source !== iframeRef.current?.contentWindow) {
          return;
        }

        if (storyboardRef.current?.deleted) {
          return;
        }

        if (event.data.error) {
          handleErrorFromIframe(
            event.data.error,
            [storyboardId],
            event.data.isConsoleError,
          );
        }
      } else if (event.data.type === "HEARTBEAT") {
        if (event.source !== iframeRef.current?.contentWindow) {
          return;
        }

        // Handle heartbeat from application storyboard using iframeId
        useIframePoolStore
          .getState()
          .handleHeartbeatReceived(appStoryboard.iframeId);
      } else if (event.data.type === "OPEN_NEW_TAB") {
        if (event.source !== iframeRef.current?.contentWindow) {
          return;
        }

        const { href } = event.data.payload;
        if (
          storyboardRef.current &&
          storyboardRef.current.type === STORYBOARD_TYPES.APPLICATION
        ) {
          duplicateStoryboard(storyboardRef.current, {
            overrides: { route: href },
          });
        }
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [appStoryboard.iframeId]);

  // Handle iframe error and retry logic
  const handleError = () => {
    console.error({
      msg: "[TEMPO DEBUG] Application storyboard iframe failed to load - error",
      storyboardId,
    });
    markApplicationStoryboardAsFailedToLoad(storyboardId);
    retryApplicationIframe(storyboardId);
  };

  useEffect(() => {
    if (!devServerLaunched) {
      return;
    }

    // Appliction storyboard is stale, force a refresh
    if (appStoryboard.needsFreshNavTreeTs) {
      // No new hot reload ts, force a refresh
      const timeout = setTimeout(() => {
        console.error(
          "[TEMPO DEBUG] Application storyboard did not get fresh data from iframe in 20 seconds, force a refresh",
          storyboardId,
          storyboardUrl,
        );
        markApplicationStoryboardAsFailedToLoad(storyboardId);
        retryApplicationIframe(storyboardId);
      }, 20 * 1000);

      return () => {
        if (timeout) {
          clearTimeout(timeout);
        }
      };
    }
  }, [appStoryboard?.needsFreshNavTreeTs, devServerLaunched]);

  useEffect(() => {
    // If the project launch status changes, reset the iframe
    if (devServerLaunched) {
      resetApplicationStoryboard(storyboardId);
    }
  }, [devServerLaunched]);

  if (!storyboardUrl || !appStoryboard) {
    return null;
  }

  return (
    <>
      {DEV_MODE && (
        <div
          style={{
            position: "fixed",
            left: 1200,
            top: 1000 - 30,
            width: 500,
          }}
        >
          {appStoryboard.iframeId}
        </div>
      )}
      {/** biome-ignore lint/a11y/useIframeTitle: this iframe is not visible to the user */}
      <iframe
        ref={iframeRef}
        id={appStoryboard.iframeId}
        key={`${appStoryboard.iframeId}-${appStoryboard.refreshKey}`}
        src={storyboardUrl}
        style={{
          left: `${
            DEV_MODE && !isDriveMode ? 1200 : appStoryboard.left || 1
          }px`,
          top: `${DEV_MODE && !isDriveMode ? 1000 : appStoryboard.top || 1}px`,
          position: "fixed",

          // Keep width and height the same so that the html renders the same way
          width: appStoryboard.width,
          height: appStoryboard.height,
          opacity: DEV_MODE || isDriveMode ? 1 : 0.001,
          pointerEvents: isDriveMode && !isPanelResizing ? "auto" : "none",
          border: "none",
          zIndex: 1000,
        }}
        onLoad={handleLoad}
        onError={handleError}
        className="bg-secondary-200 tempo-application-storyboard-iframe"
      />
    </>
  );
};
